import {
  MIN_PER_HOUR,
  SEC_PER_MIN,
  SUPPORTED_UNITS,
} from '@/helpers/unit-formatter/unit-formatter.constants';
import { UnitValue } from '@/helpers/unit-formatter/unit-formatter.types';
import {
  fpDivide,
  fpMultiply,
  secondsToDuration,
  toNumber,
} from '@/helpers/unit-formatter/unit-formatter.utils';

/**
 * Transforms time values from minutes to other time units.
 * Supports conversion to hours (hrs) and seconds (sec).
 * Uses floating-point safe arithmetic operations for accurate conversions.
 *
 * @param {UnitValue} unitValue - The time value in minutes
 * @param {any} unitValue.value - The numeric time value in minutes
 * @param {string} [unitValue.units] - The current units (should be 'min' or similar)
 * @param {string} [newUnits] - Target time unit ('hrs', 'sec')
 * @returns {UnitValue} Transformed time value in the target units
 *   - Returns original value if newUnits is not recognized
 *   - Returns original value if input value is null/undefined
 *
 * @example
 * transformMin({ value: 60, units: 'min' }, 'hrs')
 * // Returns: { value: 1, units: 'hrs' }
 *
 * @example
 * transformMin({ value: 90, units: 'min' }, 'hrs')
 * // Returns: { value: 1.5, units: 'hrs' }
 *
 * @example
 * transformMin({ value: 1, units: 'min' }, 'sec')
 * // Returns: { value: 60, units: 'sec' }
 */
export const transformMin = (unitValue: UnitValue, newUnits?: string): UnitValue => {
  const value = toNumber(unitValue.value);
  if (value == null || value == undefined) return unitValue;

  switch (newUnits?.toLowerCase()) {
    case SUPPORTED_UNITS.HRS:
      return {
        value: fpDivide(value, MIN_PER_HOUR),
        units: newUnits,
      };
    case SUPPORTED_UNITS.SEC:
      return {
        value: fpMultiply(value, SEC_PER_MIN),
        units: newUnits,
      };
    case SUPPORTED_UNITS.TIMER:
    case SUPPORTED_UNITS.DURATION:
      return {
        value: secondsToDuration(fpMultiply(value, SEC_PER_MIN)),
        units: '',
      };
    default:
      return unitValue;
  }
};
